import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getUserInfo } from '@/services/keycloakService'
import type { BrandLogo, BrandRecord } from '@/types'
import { deriveColors, normalizeHex, PARKEZE_PRIMARY } from './brandColors'

/**
 * The operator's brand record (logbook decision 26), read once the org is
 * known, cached, and put on the page as the four `--pkz-brand-*` custom
 * properties that tokens.css maps onto the site's own tokens. The modules this
 * shell mounts read the same properties, so the wizard and the desk follow the
 * brand without being told.
 *
 * The record endpoint is being upgraded. Until it is deployed it answers only
 * `{alias, displayName, brandLogoUrl, brandPrimaryColor}`; `parseRecord` reads
 * either shape into a v1 record, so nothing past this file knows the old one.
 */

/**
 * The cache key carries the record's schema version. Blobs written under an
 * older shape (the `parkeze-branding-<alias>` keys, which held
 * `partnerLogoUrl`/`brandColor`) are never read, and are removed when the
 * same alias is cached again; a future schema bumps the number.
 */
const CACHE_SCHEMA = 1
const CACHE_PREFIX = `parkeze-brand:v${CACHE_SCHEMA}:`
const LEGACY_CACHE_PREFIX = 'parkeze-branding-'

/**
 * How long a fetched record is used before it is asked for again: the
 * endpoint's own `max-age`, so a branding change shows up as soon as the
 * server would let any cache see it.
 */
const STALE_MS = 5 * 60 * 1000

/**
 * Parkeze's own record, which a page renders when no operator is known or the
 * operator's fetch fails. It is the same record the endpoint serves at alias
 * `parkeze`; the colors are what the contract's arithmetic gives for
 * `#10b981`, except the text color, #047857 (see tokens.css), and are the
 * defaults in tokens.css.
 */
export const PARKEZE_RECORD: BrandRecord = {
  version: 1,
  alias: 'parkeze',
  name: 'Parkeze',
  short_name: 'Parkeze',
  colors: {
    primary: PARKEZE_PRIMARY,
    on_primary: '#000000',
    primary_text: '#047857',
    primary_dark: '#10b981',
  },
  logo: { wordmark: null, wordmark_dark: null, mark: null, mark_dark: null },
  guest_support: { email: null, phone: null, url: null },
  powered_by: false,
  updated_at: null,
}

const BRAND_PROPERTIES = {
  '--pkz-brand-primary': 'primary',
  '--pkz-brand-on-primary': 'on_primary',
  '--pkz-brand-primary-text': 'primary_text',
  '--pkz-brand-primary-dark': 'primary_dark',
} as const

function isValidRealm(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9-]{1,64}$/.test(value)
}

function sanitizeOrgContext(value: string | null): string | null {
  if (!value || value === '*' || value === 'view-all') return null
  return isValidRealm(value) ? value : null
}

function extractOrgAliases(payload: Record<string, unknown> | null): string[] {
  if (!payload) return []
  const orgClaim = payload.organization
  if (Array.isArray(orgClaim)) {
    return orgClaim.filter((v): v is string => isValidRealm(v))
  }
  if (orgClaim && typeof orgClaim === 'object') {
    return Object.keys(orgClaim).filter((k) => isValidRealm(k))
  }
  return []
}

/** The signed-in session's organization alias, or null. No side effects. */
export function getRealmFromSession(): string | null {
  const activeOrg = sanitizeOrgContext(localStorage.getItem('parkeze.active_org'))
  if (activeOrg) return activeOrg

  const adminContext = sanitizeOrgContext(localStorage.getItem('admin-operator-context'))
  if (adminContext) return adminContext

  const tokenInfo = getUserInfo()
  const aliases = extractOrgAliases(tokenInfo as Record<string, unknown> | null)
  if (aliases.length === 1) return aliases[0]

  const realmInfoRaw = localStorage.getItem('realm-info')
  if (!realmInfoRaw) return null
  try {
    const realmInfo = JSON.parse(realmInfoRaw) as { realm?: unknown }
    const realm = realmInfo.realm
    if (!isValidRealm(realm)) return null
    if (realm === 'parkeze' || realm === 'master' || realm === 'public') return null
    return realm
  } catch {
    return null
  }
}

/** An https URL, or null. A logo is only ever served from Parkeze's asset host. */
function httpsUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null
  try {
    return new URL(value).protocol === 'https:' ? value : null
  } catch {
    return null
  }
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function parseLogo(value: unknown): BrandLogo | null {
  if (!value || typeof value !== 'object') return null
  const v = value as Record<string, unknown>
  const png = httpsUrl(v.png)
  if (!png) return null
  const aspect = typeof v.aspect === 'number' && v.aspect > 0 && Number.isFinite(v.aspect) ? v.aspect : null
  return { svg: httpsUrl(v.svg), png, aspect }
}

/**
 * Reads an answer from the record endpoint, in either shape, as a v1 record,
 * or null when it is neither.
 *
 * The legacy shape has no `colors`, so they are derived here with the
 * contract's own arithmetic (brandColors.ts); a v1 answer's colors are used as
 * given. Its one logo, `brandLogoUrl`, is a hot-linked URL of any format. It
 * is read as the wordmark, shown alone with the name as its alt text: the
 * operators who set one set their full logo (USC's is its horizontal
 * wordmark), and a wordmark squeezed into the mark's square would be
 * unreadable. The map reads it the same way.
 */
function parseRecord(data: unknown, alias: string): BrandRecord | null {
  if (!data || typeof data !== 'object') return null
  const d = data as Record<string, unknown>
  const name = text(d.name) ?? text(d.displayName)
  if (!name) return null

  const given = d.colors && typeof d.colors === 'object' ? (d.colors as Record<string, unknown>) : null
  const primary = normalizeHex(given?.primary)
  const onPrimary = normalizeHex(given?.on_primary)
  const primaryText = normalizeHex(given?.primary_text)
  const primaryDark = normalizeHex(given?.primary_dark)
  const colors =
    primary && onPrimary && primaryText && primaryDark
      ? { primary, on_primary: onPrimary, primary_text: primaryText, primary_dark: primaryDark }
      : deriveColors(given?.primary ?? d.brandPrimaryColor)

  let logo: BrandRecord['logo']
  if (d.logo && typeof d.logo === 'object') {
    const l = d.logo as Record<string, unknown>
    logo = {
      wordmark: parseLogo(l.wordmark),
      wordmark_dark: parseLogo(l.wordmark_dark),
      mark: parseLogo(l.mark),
      mark_dark: parseLogo(l.mark_dark),
    }
  } else {
    const legacy = httpsUrl(d.brandLogoUrl)
    logo = {
      wordmark: legacy ? { svg: null, png: legacy, aspect: null } : null,
      wordmark_dark: null,
      mark: null,
      mark_dark: null,
    }
  }

  const support = d.guest_support && typeof d.guest_support === 'object'
    ? (d.guest_support as Record<string, unknown>)
    : {}

  return {
    version: 1,
    alias: text(d.alias) ?? alias,
    name,
    short_name: text(d.short_name) ?? name,
    colors,
    logo,
    guest_support: { email: text(support.email), phone: text(support.phone), url: httpsUrl(support.url) },
    powered_by: typeof d.powered_by === 'boolean' ? d.powered_by : alias !== PARKEZE_RECORD.alias,
    updated_at: text(d.updated_at),
  }
}

interface CachedRecord {
  record: BrandRecord
  fetchedAt: number
}

function readCache(realm: string): CachedRecord | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + realm)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { record?: unknown; fetchedAt?: unknown }
    // Read back through the parser, so a blob that was edited or truncated
    // is ignored rather than painted.
    const record = parseRecord(parsed.record, realm)
    const fetchedAt = typeof parsed.fetchedAt === 'number' ? parsed.fetchedAt : 0
    return record ? { record, fetchedAt } : null
  } catch {
    return null
  }
}

function writeCache(realm: string, record: BrandRecord): void {
  try {
    localStorage.setItem(CACHE_PREFIX + realm, JSON.stringify({ record, fetchedAt: Date.now() }))
    localStorage.removeItem(LEGACY_CACHE_PREFIX + realm)
  } catch {
    // Storage can be full or refused (private windows); the record still renders.
  }
}

/**
 * Whether the page stays light (logbook decision 26): the logo the header
 * shows has no dark variant, so it was drawn for a light background, and the
 * page keeps one for it rather than box the logo in a white tile on a dark
 * page. That is a wordmark without wordmark_dark, else a mark without
 * mark_dark; a legacy logo is read as a wordmark and never has one. With no
 * operator logo the header shows Parkeze's mark, which reads on both, so the
 * page follows the device.
 */
function lightOnly(record: BrandRecord): boolean {
  const { wordmark, wordmark_dark, mark, mark_dark } = record.logo
  if (wordmark) return !wordmark_dark
  if (mark) return !mark_dark
  return false
}

/** The document's color-scheme meta, added the first time it is needed. */
function colorSchemeMeta(): HTMLMetaElement {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="color-scheme"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'color-scheme'
    document.head.appendChild(meta)
  }
  return meta
}

/** Sets the root's light-only class and the color scheme the browser draws with. */
function setLightOnly(light: boolean): void {
  const root = document.documentElement
  const scheme = light ? 'light' : 'light dark'
  root.classList.toggle('light-only', light)
  root.style.colorScheme = scheme
  colorSchemeMeta().content = scheme
}

/**
 * Puts the record's colors on the page root, where tokens.css and the modules
 * read them, and keeps the page light when its logo needs it. The modules
 * mounted beneath follow both: they read the colors and hold off dark mode
 * under a `light-only` ancestor.
 */
function applyBrandToRoot(record: BrandRecord): void {
  const style = document.documentElement.style
  for (const [property, key] of Object.entries(BRAND_PROPERTIES)) {
    style.setProperty(property, record.colors[key])
  }
  setLightOnly(lightOnly(record))
}

/** Takes them off again; tokens.css's defaults are Parkeze's record, which follows the device. */
function removeBrandFromRoot(): void {
  const style = document.documentElement.style
  for (const property of Object.keys(BRAND_PROPERTIES)) style.removeProperty(property)
  setLightOnly(false)
}

async function fetchRecord(realm: string, signal?: AbortSignal): Promise<BrandRecord> {
  const keycloakBase = (import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.parkeze.com').replace(/\/+$/, '')
  const endpoint = `${keycloakBase}/realms/parkeze/brand/orgs/${encodeURIComponent(realm)}`
  const res = await fetch(endpoint, { signal })
  if (!res.ok) throw new Error(`Brand record request failed: ${res.status}`)

  const record = parseRecord(await res.json(), realm)
  if (!record) throw new Error('Invalid brand record')
  writeCache(realm, record)
  return record
}

/**
 * No flash of Parkeze's colors, or of a dark page that stays light, on a
 * reload: the shell's entry (main.tsx) puts the cached record on the root
 * before React's first paint. It is called
 * there, not run when this file loads, because the booking module imports
 * this file too (through the wizard's Header) and must never touch its host's
 * root.
 */
export function applyCachedBrand(): void {
  const realm = getRealmFromSession()
  const cached = realm ? readCache(realm) : null
  if (cached) applyBrandToRoot(cached.record)
}

/**
 * The brand record in force: the session's operator's, or Parkeze's when there
 * is none or it cannot be had. The record's own `powered_by` decides the
 * Powered by Parkeze signature: true on every operator's, false on Parkeze's.
 */
export function useBranding() {
  const realm = getRealmFromSession()
  const cached = realm ? readCache(realm) : null

  const { data, isLoading } = useQuery({
    queryKey: ['brand-record', CACHE_SCHEMA, realm],
    queryFn: ({ signal }) => fetchRecord(realm!, signal),
    enabled: !!realm,
    initialData: cached?.record,
    // The cached record is as old as its fetch, not as new as this page, so
    // a stale one is refetched (and shown meanwhile).
    initialDataUpdatedAt: cached?.fetchedAt,
    staleTime: STALE_MS,
    retry: 1,
  })

  const brand = data ?? PARKEZE_RECORD

  useEffect(() => {
    applyBrandToRoot(brand)
    return removeBrandFromRoot
  }, [brand])

  return { realm, brand, isLoading }
}
